import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * Rich loading skeleton for the school-admin console.
 * Mirrors the hero → KPI ring → tabs layout so the paint feels stable.
 */
export function DashboardSkeleton({ label = "Warming up your console…" }: { label?: string }) {
  return (
    <div className="animate-in fade-in-50 space-y-4" aria-busy="true" aria-label={label}>
      <Card className="relative overflow-hidden bg-gradient-to-br from-emerald-900/90 via-emerald-800 to-emerald-950 p-6 text-emerald-50">
        <div className="flex items-center justify-between gap-6">
          <div className="space-y-3">
            <Skeleton className="h-4 w-40 bg-emerald-50/10" />
            <Skeleton className="h-8 w-72 bg-emerald-50/20" />
            <Skeleton className="h-3 w-96 bg-emerald-50/10" />
          </div>
          <div className="flex items-center gap-4">
            {[0, 1, 2].map((i) => (
              <div key={i} className="grid h-24 w-24 place-items-center rounded-full border border-emerald-50/20 bg-emerald-950/30">
                <Skeleton className="h-6 w-10 bg-emerald-50/20" />
              </div>
            ))}
          </div>
        </div>
        <div className="pointer-events-none absolute inset-0 opacity-30 [background:radial-gradient(circle_at_20%_10%,white,transparent_40%)]" />
      </Card>

      <div className="grid gap-3 md:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Card key={i} className="p-4">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="mt-3 h-6 w-24" />
            <Skeleton className="mt-2 h-2 w-full" />
          </Card>
        ))}
      </div>

      <Card className="p-4">
        <div className="flex gap-2">
          {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-8 w-24" />)}
        </div>
        <div className="mt-4 grid gap-3 md:grid-cols-3">
          <Skeleton className="h-56" />
          <Skeleton className="h-56" />
          <Skeleton className="h-56" />
        </div>
      </Card>

      <p className="text-center text-xs text-muted-foreground">{label}</p>
    </div>
  );
}
